import {
  type ChallengeActivityCategory,
  ChallengeCompletionStatus,
  ChallengeGoalType,
  ChallengeStatus,
  ChallengeTaskType,
  ChallengeVerification,
} from "@fitness-app/data-models/entities/Challenge";
import { ChallengeFeedMode } from "@fitness-app/data-models/entities/ChallengeFeed";

export const SEEDED_CHALLENGE_ID = "c0000000-0000-4000-8000-000000000010";

export const challengeStatusLabels: Record<ChallengeStatus, string> = {
  [ChallengeStatus.Upcoming]: "Nadchodzące",
  [ChallengeStatus.Active]: "Aktywne",
  [ChallengeStatus.Finished]: "Zakończone",
  [ChallengeStatus.Archived]: "Zarchiwizowane",
};

export const challengeStatusColors: Record<ChallengeStatus, string> = {
  [ChallengeStatus.Upcoming]: "blue",
  [ChallengeStatus.Active]: "green",
  [ChallengeStatus.Finished]: "default",
  [ChallengeStatus.Archived]: "default",
};

export const challengeTaskTypeLabels: Record<ChallengeTaskType, string> = {
  [ChallengeTaskType.PlanActivity]: "Aktywność z planu",
  [ChallengeTaskType.ExtraTask]: "Zadanie dodatkowe",
  [ChallengeTaskType.Habit]: "Nawyk",
  [ChallengeTaskType.NumericGoal]: "Cel liczbowy",
  [ChallengeTaskType.ExerciseRecording]: "Trening z wideo",
};

export const challengeVerificationLabels: Record<ChallengeVerification, string> = {
  [ChallengeVerification.Auto]: "Automatyczna",
  [ChallengeVerification.SelfReport]: "Samoocena",
  [ChallengeVerification.ProofUpload]: "Dowód (zdjęcie)",
  [ChallengeVerification.TrainerApproval]: "Akceptacja trenera",
};

export const completionStatusLabels: Record<ChallengeCompletionStatus, string> = {
  [ChallengeCompletionStatus.Pending]: "Oczekuje",
  [ChallengeCompletionStatus.Approved]: "Zaakceptowane",
  [ChallengeCompletionStatus.Rejected]: "Odrzucone",
  [ChallengeCompletionStatus.Auto]: "Automatyczne",
};

export const completionStatusColors: Record<ChallengeCompletionStatus, string> = {
  [ChallengeCompletionStatus.Pending]: "gold",
  [ChallengeCompletionStatus.Approved]: "green",
  [ChallengeCompletionStatus.Rejected]: "red",
  [ChallengeCompletionStatus.Auto]: "blue",
};

// planActivity tasks map to real ClientDayActivity categories only. `habits`/`challenges` are rating
// meta-categories (no ClientDayActivity source — they'd score 0), so they're excluded from this picker.
export const challengeActivityCategoryLabels: Record<
  Exclude<ChallengeActivityCategory, "habits" | "challenges">,
  string
> = {
  workouts: "Treningi",
  mealsTracked: "Oznaczone posiłki",
  bodyMeasurements: "Pomiary ciała",
  weightMeasurements: "Pomiary wagi",
  bodyPhotos: "Zdjęcia sylwetki",
  surveyAnswers: "Odpowiedzi w ankietach",
  consultations: "Konsultacje",
  messages: "Wiadomości",
  logins: "Logowania",
  hydration: "Nawodnienie (ml)",
};

export const challengeTaskTypeOptions = Object.values(ChallengeTaskType).map((value) => ({
  value,
  label: challengeTaskTypeLabels[value],
}));

export const challengeVerificationOptions = Object.values(ChallengeVerification).map((value) => ({
  value,
  label: challengeVerificationLabels[value],
}));

// Which verification modes actually have a data source for each task type.
// `auto` is only meaningful for planActivity (the scoring engine credits it from the client's recorded
// ClientActivity by category). For the other types there is no automatic source — points come from the
// client's self-report / uploaded proof / trainer approval, so `auto` is intentionally not offered.
export const verificationByTaskType: Record<ChallengeTaskType, ChallengeVerification[]> = {
  [ChallengeTaskType.PlanActivity]: [ChallengeVerification.Auto, ChallengeVerification.SelfReport],
  [ChallengeTaskType.Habit]: [ChallengeVerification.SelfReport],
  [ChallengeTaskType.NumericGoal]: [ChallengeVerification.SelfReport, ChallengeVerification.TrainerApproval],
  [ChallengeTaskType.ExtraTask]: [
    ChallengeVerification.SelfReport,
    ChallengeVerification.ProofUpload,
    ChallengeVerification.TrainerApproval,
  ],
  // exerciseRecording reads the recording lifecycle: trainerApproval counts once the trainer rates it
  // (the contest default — ties scoring to the rating, anti-abuse); auto counts a bare `recorded` upload.
  [ChallengeTaskType.ExerciseRecording]: [ChallengeVerification.TrainerApproval, ChallengeVerification.Auto],
};

export const defaultVerificationByTaskType: Record<ChallengeTaskType, ChallengeVerification> = {
  [ChallengeTaskType.PlanActivity]: ChallengeVerification.Auto,
  [ChallengeTaskType.Habit]: ChallengeVerification.SelfReport,
  [ChallengeTaskType.NumericGoal]: ChallengeVerification.SelfReport,
  [ChallengeTaskType.ExtraTask]: ChallengeVerification.SelfReport,
  [ChallengeTaskType.ExerciseRecording]: ChallengeVerification.TrainerApproval,
};

export const verificationOptionsForTaskType = (type: ChallengeTaskType) =>
  verificationByTaskType[type].map((value) => ({ value, label: challengeVerificationLabels[value] }));

// TRE-1655 — feed mode per challenge. `none` keeps the challenge exactly as it is today.
export const challengeFeedModeLabels: Record<ChallengeFeedMode, string> = {
  [ChallengeFeedMode.None]: "Wyłączony",
  [ChallengeFeedMode.Readonly]: "Tylko ogłoszenia (uczestniczki czytają)",
  [ChallengeFeedMode.Comments]: "Ogłoszenia + komentarze",
  [ChallengeFeedMode.Full]: "Pełny (uczestniczki też publikują)",
};

export const challengeFeedModeOptions = Object.values(ChallengeFeedMode).map((value) => ({
  value,
  label: challengeFeedModeLabels[value],
}));

export const CHALLENGE_FEED_MODE_TOOLTIP =
  "Wyłączony — brak tablicy. Tylko ogłoszenia — posty dodaje wyłącznie trener, uczestniczki czytają i " +
  "mogą reagować. Ogłoszenia + komentarze — dodatkowo komentują. Pełny — uczestniczki mogą też " +
  "publikować własne posty.";

export const challengeGoalTypeLabels: Record<ChallengeGoalType, string> = {
  [ChallengeGoalType.Leaderboard]: "Ranking",
  [ChallengeGoalType.Threshold]: "Próg punktowy",
  [ChallengeGoalType.Both]: "Próg + ranking",
};

/** dayjs numbering (0 = Sunday … 6 = Saturday), rendered Monday-first for Polish users. */
export const challengeEventDayOfWeekOptions = [
  { value: 1, label: "Pon" },
  { value: 2, label: "Wt" },
  { value: 3, label: "Śr" },
  { value: 4, label: "Czw" },
  { value: 5, label: "Pt" },
  { value: 6, label: "Sob" },
  { value: 0, label: "Niedz" },
];

export const challengeActivityCategoryOptions = (
  Object.keys(challengeActivityCategoryLabels) as Exclude<ChallengeActivityCategory, "habits" | "challenges">[]
).map((value) => ({
  value,
  label: challengeActivityCategoryLabels[value],
}));
